/** Visitor-facing gym content keyed by BCP 47 locale code. */
export type GymContentTranslations = Record<
  string,
  {
    equipmentDescription?: string | null;
    directionsDescription?: string | null;
  }
>;

export function localizedGymContent(
  translations: GymContentTranslations | null | undefined,
  field: 'equipmentDescription' | 'directionsDescription',
  locale: string,
  legacy?: string | null,
): string {
  const normalized = locale.toLowerCase();
  const entries = Object.entries(translations ?? {});
  const read = (code: string) =>
    entries.find(([key]) => key.toLowerCase() === code)?.[1][field]?.trim();
  return (
    read(normalized) ||
    read(normalized.split('-')[0] ?? normalized) ||
    read('cs') ||
    legacy?.trim() ||
    read('en') ||
    entries.map(([, value]) => value[field]?.trim()).find(Boolean) ||
    ''
  );
}

export interface Gym {
  id: string;
  tenantId: string;
  tenant?: {
    id?: string;
    name: string;
    slug: string;
  };
  name: string;
  slug: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  postcode: string;
  country: string;
  contactEmail: string | null;
  contactPhone: string | null;
  contentTranslations?: GymContentTranslations;
  equipmentDescription: string | null;
  directionsDescription: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string;
  isActive: boolean;
  capacity: number;
  slotDurationMinutes: number;
  cancellationDeadlineHours: number;
  accessCodeValidBeforeMin: number;
  accessCodeValidAfterMin: number;
  slotBufferMinutes: number;
  startingPriceInSmallestUnit?: number;
  startingPriceCurrency?: string;
  lockConfig: Record<string, unknown>;
  lockAlertPhoneNumbers?: string[];
  operatingHours?: OperatingHours[];
  createdAt: Date;
  updatedAt: Date;
}

export interface OperatingHours {
  id: string;
  gymId: string;
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

export interface GymPhoto {
  id: string;
  position: number;
  thumbnailUrl: string;
  fullUrl: string;
  thumbnailWidth: number;
  thumbnailHeight: number;
  width: number;
  height: number;
}
